import React from 'react';

const STAGE_LABELS = {
  in_progress: 'Mulai',
  diracik: 'Racik',
  maserasi: 'Maserasi',
  menunggu_filling: 'Filling',
  filling: 'Filling',
  menunggu_packing: 'Packing',
  packing: 'Packing',
  siap_kirim: 'Kirim',
  done: 'Selesai',
};

export default function ProgressRing({ percentage = 0, size = 80, strokeWidth = 6, className = '', isStageMode = false, stageName = '' }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const getColor = () => {
    if (isStageMode) {
      if (percentage >= 100) return 'hsl(142, 60%, 45%)';
      if (percentage >= 70) return 'hsl(280, 55%, 55%)';
      if (percentage >= 40) return 'hsl(210, 70%, 55%)';
      return 'hsl(25, 75%, 55%)';
    }
    if (percentage >= 100) return 'hsl(142, 60%, 45%)';
    if (percentage >= 60) return 'hsl(45, 80%, 55%)';
    if (percentage > 0) return 'hsl(25, 60%, 48%)';
    return 'hsl(30, 15%, 75%)';
  };

  const stageLabel = STAGE_LABELS[stageName] || '';

  if (isStageMode) {
    // Stage mode: show a different visual — gradient ring + stage label
    const color = getColor();
    const gradId = `stage-grad-${Math.random().toString(36).slice(2, 8)}`;
    return (
      <div className={`relative inline-flex items-center justify-center ${className}`}>
        <svg width={size} height={size} className="-rotate-90">
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={color} />
              <stop offset="100%" stopColor="hsl(280, 55%, 60%)" />
            </linearGradient>
          </defs>
          {/* Background track with dashes for checkpoint feel */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="hsl(30, 15%, 90%)"
            strokeWidth={strokeWidth}
            strokeDasharray="4 4"
          />
          {/* Active arc with gradient */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth={strokeWidth + 1}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-700 ease-out"
            style={{ filter: 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.3))' }}
          />
        </svg>
        <div className="absolute flex flex-col items-center leading-none">
          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color }}>
            {stageLabel}
          </span>
          <span className="text-xs font-semibold text-foreground mt-0.5">
            {Math.round(percentage)}%
          </span>
        </div>
      </div>
    );
  }

  // Default mode: Elegant Gradient Ring (like the requested design)
  const isHealthy = percentage >= 80;
  const isWarning = percentage >= 50 && percentage < 80;
  
  // Define gradient colors based on percentage
  const gradStart = isHealthy ? '#34d399' : isWarning ? '#fbbf24' : '#fb923c';
  const gradEnd = isHealthy ? '#10b981' : isWarning ? '#f59e0b' : '#f43f5e';
  const textColor = isHealthy ? 'text-emerald-600' : isWarning ? 'text-amber-500' : 'text-rose-500';
  
  const defsId = `grad-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <div className={`relative inline-flex flex-col items-center justify-center ${className}`}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id={defsId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={gradStart} />
            <stop offset="100%" stopColor={gradEnd} />
          </linearGradient>
        </defs>
        {/* Soft background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          className="text-slate-100"
          strokeWidth={strokeWidth}
        />
        {/* Gradient foreground arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${defsId})`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-xl font-black tracking-tighter ${textColor}`}>
          {Math.round(percentage)}<span className="text-sm font-bold opacity-70">%</span>
        </span>
      </div>
    </div>
  );
}